import {
  createChart,
  createTextWatermark,
  CandlestickSeries,
  BarSeries,
  AreaSeries,
  HistogramSeries,
  LineSeries
} from 'lightweight-charts'
import {
  calculateEMA,
  calculateSMA,
  buildVolumeData,
  buildWatermarkLines,
  buildChartOptions,
  buildPriceSeriesStyleOptions,
  buildVolumeSeriesOptions,
  buildVolumeScaleOptions,
  buildMaSeriesOptions,
  buildStageBands,
  stageCssCode,
  hexToRgba
} from './chart_utils'
import { cssVar } from './chart_theme'
import { TrendGaugeBand } from './trend_gauge_band'

// Alpha for the Weinstein stage background tint — light enough that candles
// and MAs stay legible on top in both themes.
const STAGE_TINT_ALPHA = 0.16

// Stage key -> translucent tint of the same --app-stage-*-line palette
// variable the /leaders cycle diagram uses, so the chart tint follows the
// palette (and dark mode) with no color table duplicated here.
function stageTintColor(stageKey) {
  const code = stageCssCode(stageKey)
  if (!code) return null
  return hexToRgba(cssVar(`--app-stage-${code}-line`, ''), STAGE_TINT_ALPHA)
}

// Candles + volume + user-configured moving averages, drawn into a container.
//
// Extracted from ticker_chart_controller so the forum's inline [chart:TICKER]
// embeds draw exactly the same chart as the $TICKER popup — the two differ only
// in where they put the container, and a second copy of this would drift.
//
// Kept out of chart_utils.js deliberately: that module is pure option-builders
// with no lightweight-charts import, and several Jest suites lean on it staying
// that way.
//
// Returns a handle with destroy(), which callers must invoke on disconnect —
// lightweight-charts holds a canvas and a ResizeObserver that do not get
// collected on their own.
export function renderPriceChart(container, data, stockInfo, options = {}) {
  const { logScale = false, maConfig = [], bottomMargin = 0.2, stages = null, chartType = 'candle', upColor = null, downColor = null } = options

  const width = container.clientWidth || 800
  const height = container.clientHeight || 450

  container.innerHTML = ''

  const chart = createChart(container, buildChartOptions({ width, height, logScale, bottomMargin }))

  const seriesClasses = { candle: CandlestickSeries, bar: BarSeries, line: LineSeries, area: AreaSeries }
  const SeriesType = seriesClasses[chartType] || CandlestickSeries
  const series = chart.addSeries(SeriesType, {
    ...buildPriceSeriesStyleOptions(chartType, upColor, downColor),
    priceScaleId: 'right'
  })
  // Line/area series take {time, value} points; candle/bar take raw OHLC
  const seriesData = (chartType === 'line' || chartType === 'area')
    ? data.map((item) => ({ time: item.time, value: item.close }))
    : data
  series.setData(seriesData)

  // Weinstein stage background tint (the /leaders popup) — contiguous
  // same-stage runs painted full-height behind the candles, reusing the
  // trend-gauge band primitive (zOrder bottom).
  if (stages?.length) {
    try {
      const bands = buildStageBands(stages, stageTintColor)
      if (bands.length) series.attachPrimitive(new TrendGaugeBand({ bands, data }))
    } catch {
      // A chart without the stage tint is still worth showing.
    }
  }

  try {
    const volumeSeries = chart.addSeries(HistogramSeries, buildVolumeSeriesOptions(upColor))
    chart.priceScale('volume').applyOptions(buildVolumeScaleOptions())
    volumeSeries.setData(buildVolumeData(data, upColor, downColor))
  } catch {
    // A chart without volume is still worth showing.
  }

  ;(maConfig || []).forEach((config) => {
    if (!config.enabled || data.length < config.period) return

    const maData = config.type === 'ema' ? calculateEMA(data, config.period) : calculateSMA(data, config.period)
    const line = chart.addSeries(LineSeries, buildMaSeriesOptions({
      color: config.color,
      lineWidth: parseFloat(config.line_width) || 1.0
    }))
    line.setData(maData)
  })

  if (stockInfo) {
    const lines = buildWatermarkLines(stockInfo)
    if (lines.length) {
      try {
        createTextWatermark(chart.panes()[0], { horzAlign: 'center', vertAlign: 'center', lines })
      } catch {
        // Watermark is decorative.
      }
    }
  }

  chart.timeScale().fitContent()

  const resizeObserver = new ResizeObserver((entries) => {
    if (entries.length === 0) return
    const { width, height } = entries[0].contentRect
    if (width > 0 && height > 0) chart.applyOptions({ width, height })
  })
  resizeObserver.observe(container)

  return {
    chart,
    destroy() {
      resizeObserver.disconnect()
      chart.remove()
    }
  }
}
